/**
 * AdminArticles — list view for the self-service Articles CMS.
 * Combines WIM Blog + Care News (Draft and Published) via GET
 * /api/admin/articles, with section/status filter tabs, a text search,
 * and Edit / View links per row. "New article" opens AdminArticleEditor
 * in create mode.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, ExternalLink, Search, Send, XCircle, Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";
import { formatUkDateTime } from "../../lib/ukTime";

const SECTION_TABS = [
  { value: "all", label: "All" },
  { value: "wim_blog", label: "WIM Blog" },
  { value: "care_news", label: "Care News" },
  { value: "pro_carer_series", label: "Advantage Series" },
];

const SECTION_LABELS = {
  wim_blog: "WIM Blog",
  care_news: "Care News",
  pro_carer_series: "Advantage Series",
};
const SECTION_PILL_CLASSES = {
  wim_blog: "bg-purple/10 text-purple",
  care_news: "bg-teal/10 text-teal",
  pro_carer_series: "bg-amber-100 text-amber-800",
};
const SECTION_URL_SEGMENT = { wim_blog: "wim-blog", care_news: "care-news", pro_carer_series: "advantage-series" };

const STATUS_TABS = [
  { value: "all", label: "All statuses" },
  { value: "published", label: "Published" },
  { value: "scheduled", label: "Scheduled" },
  { value: "draft", label: "Draft" },
];

function articleApiBase(section) {
  return section === "care_news" ? "/api/admin/news" : "/api/admin/blog";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function publicPathFor(article) {
  if (article.section === "wim_blog") return `/wim-blog/${article.slug}`;
  if (article.section === "pro_carer_series") return `/workspace/pro-carer-circle/advantage-series/${article.slug}`;
  return `/care-news/${article.slug}`;
}

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [sectionTab, setSectionTab] = useState("all");
  const [statusTab, setStatusTab] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    loadArticles();
  }, []);

  async function loadArticles() {
    try {
      const { data } = await api.get("/api/admin/articles");
      setArticles(data.articles || []);
      setLoaded(true);
    } catch {
      setErrored(true);
      setLoaded(true);
    }
  }

  const publishNow = async (a) => {
    try {
      await api.post(`${articleApiBase(a.section)}/${a.slug}/publish-now`);
      toast.success("Published now.");
      loadArticles();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not publish this article.");
    }
  };

  const cancelSchedule = async (a) => {
    try {
      await api.post(`${articleApiBase(a.section)}/${a.slug}/cancel-schedule`);
      toast.success("Schedule cancelled — moved back to Draft.");
      loadArticles();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not cancel this schedule.");
    }
  };

  const togglePin = async (a) => {
    const nextPinned = !a.is_featured;
    try {
      await api.post(`${articleApiBase(a.section)}/${a.slug}/pin`, { is_featured: nextPinned });
      toast.success(nextPinned ? "Pinned to the top of its section." : "Unpinned.");
      setArticles((list) => list.map((item) => (item.slug === a.slug && item.section === a.section ? { ...item, is_featured: nextPinned } : item)));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update pin status.");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      if (sectionTab !== "all" && a.section !== sectionTab) return false;
      if (statusTab !== "all" && (a.status || "published") !== statusTab) return false;
      if (q && !`${a.title} ${a.category}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [articles, sectionTab, statusTab, query]);

  const draftCount = articles.filter((a) => a.status === "draft").length;
  const scheduledCount = articles.filter((a) => a.status === "scheduled").length;

  return (
    <div data-testid="admin-articles-page">
      <PageHero
        eyebrow="JCC Admin"
        title="Articles"
        description="Create and manage WIM Blog and Care News articles — draft, publish and edit content without a deploy."
      >
        <div className="flex items-center gap-2">
          <Link to="/admin" className="btn-outline text-sm" data-testid="admin-articles-back-home">
            <ArrowLeft className="w-4 h-4" /> Admin home
          </Link>
          <Link to="/admin/articles/new" className="btn-teal text-sm" data-testid="admin-articles-new-btn">
            <Plus className="w-4 h-4" /> New article
          </Link>
        </div>
      </PageHero>

      <section className="section pt-8 pb-24">
        <div className="container-jcc max-w-5xl">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-2" data-testid="admin-articles-section-tabs">
              {SECTION_TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSectionTab(t.value)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
                    sectionTab === t.value ? "bg-teal text-white border-teal" : "border-slate-200 text-ink-muted hover:border-teal/40"
                  }`}
                  data-testid={`admin-articles-section-tab-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title or category…"
                className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal/30"
                data-testid="admin-articles-search-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6" data-testid="admin-articles-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setStatusTab(t.value)}
                className={`text-sm font-semibold pb-2 border-b-2 transition-colors ${
                  statusTab === t.value ? "border-purple text-purple" : "border-transparent text-ink-muted hover:text-ink"
                }`}
                data-testid={`admin-articles-status-tab-${t.value}`}
              >
                {t.label}
                {t.value === "draft" && draftCount > 0 && ` (${draftCount})`}
                {t.value === "scheduled" && scheduledCount > 0 && ` (${scheduledCount})`}
              </button>
            ))}
          </div>

          {!loaded ? (
            <p className="text-ink-muted text-sm py-10 text-center" data-testid="admin-articles-loading">Loading articles…</p>
          ) : errored ? (
            <p className="text-red-600 text-sm py-10 text-center" data-testid="admin-articles-error">Could not load articles.</p>
          ) : filtered.length === 0 ? (
            <p className="text-ink-muted text-sm py-10 text-center border border-dashed border-slate-200 rounded-xl" data-testid="admin-articles-empty">
              No articles match this filter.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="admin-articles-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-ink-muted">
                  <tr>
                    <th className="p-3">Title</th>
                    <th className="p-3">Section</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Published</th>
                    <th className="p-3">Pinned</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr key={`${a.section}-${a.slug}`} className="border-t border-slate-100" data-testid={`admin-articles-row-${a.slug}`}>
                      <td className="p-3 font-medium text-ink max-w-xs truncate">{a.title}</td>
                      <td className="p-3">
                        <span className={`pill text-xs ${SECTION_PILL_CLASSES[a.section] || "bg-slate-100 text-ink-muted"}`}>
                          {SECTION_LABELS[a.section] || a.section}
                        </span>
                      </td>
                      <td className="p-3 text-ink-muted">{a.category || "—"}</td>
                      <td className="p-3">
                        <span
                          className={`pill text-xs font-semibold ${
                            a.status === "draft"
                              ? "bg-slate-200 text-ink"
                              : a.status === "scheduled"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-green-100 text-green-800"
                          }`}
                          data-testid={`admin-articles-status-${a.slug}`}
                        >
                          {a.status === "draft" ? "Draft" : a.status === "scheduled" ? "Scheduled" : "Published"}
                        </span>
                        {a.status === "scheduled" && a.scheduled_publish_at && (
                          <div className="mt-1 text-xs text-ink-soft" data-testid={`admin-articles-scheduled-for-${a.slug}`}>
                            {formatUkDateTime(a.scheduled_publish_at)}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-ink-muted">{fmtDate(a.published_at)}</td>
                      <td className="p-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => togglePin(a)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                            a.is_featured ? "bg-purple/10 text-purple" : "bg-slate-100 text-ink-muted"
                          }`}
                          data-testid={`admin-articles-pin-toggle-${a.section}-${a.slug}`}
                          title={a.is_featured ? "Pinned — click to unpin" : "Click to pin to top of its section"}
                          aria-label={a.is_featured ? "Unpin this article" : "Pin this article to the top of its section"}
                        >
                          {a.is_featured ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Link
                          to={`/admin/articles/${SECTION_URL_SEGMENT[a.section] || "care-news"}/${a.slug}/edit`}
                          className="inline-flex items-center gap-1 text-teal font-medium mr-3"
                          data-testid={`admin-articles-edit-${a.slug}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Link>
                        {a.status === "scheduled" && (
                          <>
                            <button
                              type="button"
                              onClick={() => publishNow(a)}
                              className="inline-flex items-center gap-1 text-green-700 font-medium mr-3"
                              data-testid={`admin-articles-publish-now-${a.slug}`}
                            >
                              <Send className="w-3.5 h-3.5" /> Publish now
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelSchedule(a)}
                              className="inline-flex items-center gap-1 text-red-600 font-medium mr-3"
                              data-testid={`admin-articles-cancel-schedule-${a.slug}`}
                            >
                              <XCircle className="w-3.5 h-3.5" /> Cancel schedule
                            </button>
                          </>
                        )}
                        {a.status === "published" && (
                          <a
                            href={publicPathFor(a)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-ink-muted hover:text-ink"
                            data-testid={`admin-articles-view-${a.slug}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
